import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import { Button, LoadingIcon } from "@mwi/ui";

import { RootState } from "store";
import ErrorMessageWithRetry from "../../components/ErrorMessageWithRetry";

import ConversationListItemCard from "./ConversationListItem";
import { PageHeader } from "components/PageHeader";
import { getAllConversations } from "slices/messages";

type ConversationListSectionProps = {
  onSelectConversation: (conversationId: string) => void;
  selectedConversation: string | undefined;
};

const ConversationsListSection = ({
  onSelectConversation,
  selectedConversation,
}: ConversationListSectionProps): JSX.Element => {
  const dispatch = useDispatch();

  const { error, loading, conversationsStore, draftConversation } = useSelector(
    (state: RootState) => ({
      error: state.messages.isErrorConversations,
      loading: state.messages.isLoadingConversations,
      conversationsStore: state.messages.conversations,
      draftConversation: state.messages.draftConversation,
    }),
  );

  const conversations = useMemo(
    () =>
      Object.values(conversationsStore).sort(
        (a, b) => b.lastUpdated - a.lastUpdated,
      ),
    [conversationsStore],
  );
  const totalUnread = useMemo(
    () =>
      conversations.reduce(
        (accumulator, conversation) => accumulator + conversation.numUnread,
        0,
      ),
    [conversations],
  );
  const hasConversations = useMemo(
    () => Boolean(conversations?.length),
    [conversations],
  );

  const getContent = () => {
    if (error)
      return (
        <ErrorMessageWithRetry
          title="Something went wrong"
          message="We couldn't fetch your conversations."
          onRetry={() => dispatch(getAllConversations())}
        />
      );
    if (loading)
      return (
        <div className="flex flex-col flex-grow items-center justify-center">
          <LoadingIcon className="w-10 h-10 text-grey" />
        </div>
      );
    if (conversations?.length) {
      return Object.values(conversations).map((conversation) => (
        <ConversationListItemCard
          data-test-id={`ConversationListItemCard_${conversation.id}`}
          key={conversation.id}
          isSelected={
            conversation.id === selectedConversation ||
            Boolean(conversation.numUnread)
          }
          conversation={conversation}
          onClick={() => onSelectConversation(conversation.id)}
        />
      ));
    }
    if (!hasConversations && !selectedConversation) {
      return (
        <div className="flex flex-col flex-grow items-center justify-center">
          <p>No conversations</p>
        </div>
      );
    }
  };

  return (
    <div
      className={classNames(
        "h-full overflow-hidden relative flex flex-col flex-grow tablet:col-span-4 pb-4 mb-20 bg-white tablet:shadow-default",
        { "hidden tablet:flex": selectedConversation },
      )}
    >
      <PageHeader
        className="tablet:hidden"
        title={`Messages ${totalUnread ? `(${totalUnread})` : ""}`}
      >
        <Button
          data-test-id="ConversationsListSection_NewConversationButton"
          type="link"
          to="/messages/new"
          variant="primary"
          size="small"
        >
          New conversation
        </Button>
      </PageHeader>

      <div className="hidden tablet:flex flex-row justify-between items-center mb-8 tablet:mb-2 px-4 tablet:p-6 tablet:pb-0">
        <h2 className="text-2xl tablet:text-xl font-semibold tablet:font-bold overflow-hidden text-ellipsis whitespace-nowrap">
          Messages {totalUnread ? `(${totalUnread})` : null}
        </h2>
        <Button
          data-test-id="ConversationsListSection_NewConversationButton"
          type="link"
          to="/messages/new"
          variant="primary"
          size="small"
        >
          New conversation
        </Button>
      </div>

      <div className="h-full overflow-auto relative flex flex-col flex-grow bg-white shadow-default tablet:shadow-none p-4 tablet:px-6 overflow-auto">
        {draftConversation && (
          <ConversationListItemCard
            data-test-id={`ConversationListItemCard_${draftConversation.id}`}
            key={draftConversation.id}
            isSelected
            draft
            conversation={draftConversation}
          />
        )}
        {getContent()}
      </div>
    </div>
  );
};

export default ConversationsListSection;
